import { nutrimentsInterface, StatsInterface } from "dataProvider/Produit";
import { PointOptionsObject } from "highcharts";

interface Unite {
    unite: string;
    type: string;
}

const debug = false;
const fontSize = "12px";
const boxplotByChart = 26;

/**
 * Ce tri est à adapter avec la requête SQL qui va avec
 * @see server/src/Action/ConstituantStd/Liste.php
 */
const nutrimentsPriority = [
    67,    // Valeur energetique kj
    66,    // Valeur energetique kcal
    40,    // Matieres grasses
    9,     // Acides gras satures
    31,    // Glucides
    62,    // Sucres
    53,    // Proteines
    57,    // Sel
    25,    // Fibres alimentaires
    // Puis tous les autres dans l'ordre alphabétique.
];

const getMinMaxSeries = (nutriments: nutrimentsInterface | nutrimentsInterface[], min: number = 0, max: number = 0) => {
    if (!Array.isArray(nutriments)) {
        nutriments = [nutriments];
    }

    nutriments.forEach((nutriment: nutrimentsInterface) => {
        if (min > nutriment.quartiles.min) {
            min = nutriment.quartiles.min;
        }
        if (max < nutriment.quartiles.max) {
            max = nutriment.quartiles.max;
        }

        nutriment.quartiles.outliers.forEach((outlier) => {
            if (min > outlier) {
                min = outlier;
            }
            if (max < outlier) {
                max = outlier;
            }
        });
    });
    return [min, max];
};

/**
 * ordre sous forme de tableau : x,low,q1,median,q3,high
 *
 * @param nutriment
 * @param color
 * @returns
 */
const formatBoxPlot = (nutriment: nutrimentsInterface, color: string = "#08546C"): PointOptionsObject => {
    return {
        // x: nutriment.observations,
        low: nutriment.quartiles.low,
        q1: nutriment.quartiles.quartile1,
        median: nutriment.quartiles.median,
        q3: nutriment.quartiles.quartile3,
        high: nutriment.quartiles.high,
        name: nutriment.nutriment, // Nom repris dans le tooltip ainsi que la liste des données
        color: color,
    };
};

/**
 * Retourne la légende Y pour les différents types d'unités de la séries
 *
 * @param unites array
 * @returns string
 */
const getYLegend = (unites: Unite[]) => {
    let finalTypes: string[] = [],
        finalUnites: string[] = [];

    if (debug) {
        // console.log("Unités détéctées", unites);
    }

    if (unites.length === 0) {
        return "N/C";
    }

    if (unites.length === 1) {
        return `${unites[0].unite}/100${unites[0].type}`;
    }

    unites.forEach((unite: Unite) => {
        if (!finalUnites.includes(unite.unite)) {
            finalUnites.push(unite.unite);
        }
        if (!finalTypes.includes(unite.type)) {
            finalTypes.push(unite.type);
        }
    });

    if (finalUnites.length > 1) {
        return `(${finalUnites.join(" ou ")})/100${finalTypes.join(" ou 100")}`;
    } else {
        return `${finalUnites.join(" ou ")}/100${finalTypes.join(" ou 100")}`;
    }
};

/**
 * Découpage des series pour avoir un maximum de x Boxplot par graphe
 *
 * @param data
 * @returns DevideSerieInterface
 */
const devideSerieSecteur = (data: any) => {
    let allResult: DevideSerieInterface[] = [];

    data.forEach((bySecteur: any) => {
        bySecteur = Object.values(bySecteur);
        let result: DevideSerieInterface = {
            total: bySecteur.length || 0,
            totalBySerie: 0,
            totalSeries: 1,
            rows: [],
        };
        let rows: any[] = [];

        let chunk: number = Math.ceil(result.total / boxplotByChart);
        result.totalSeries = chunk;
        result.totalBySerie = Math.ceil(result.total / chunk);

        let i = 0;
        bySecteur.forEach((row: any) => {
            row[0].filter_marque = `${row[0].row.type_marque}-${row[0].row.annee_collecte}`;
            rows.push(row);
            i++;
            if (i >= result.totalBySerie) {
                result.rows.push(rows);
                rows = [];
                i = 0;
            }
        });

        if (rows.length > 0) {
            result.rows.push(rows);
        }

        allResult.push(result);
    });

    return allResult;
};

const devideSerieSecteurMap = (data: Map<string, Map<string, any[]>>) => {
    let allResult: DevideSerieInterface[] = [];

    data.forEach((bySecteur: Map<string, any[]>) => {
        let result: DevideSerieInterface = {
            total: bySecteur.size || 0,
            totalBySerie: 0,
            totalSeries: 1,
            rows: [],
        };
        let rows: any[] = [];

        let chunk: number = Math.ceil(result.total / boxplotByChart);
        result.totalSeries = chunk;
        result.totalBySerie = Math.ceil(result.total / chunk);

        let i = 0;
        bySecteur.forEach((row: any) => {
            row[0].filter_marque = `${row[0].row.type_marque}-${row[0].row.annee_collecte}`;
            rows.push(row);
            i++;
            if (i >= result.totalBySerie) {
                result.rows.push(rows);
                rows = [];
                i = 0;
            }
        });

        if (rows.length > 0) {
            result.rows.push(rows);
        }

        allResult.push(result);
    });

    return allResult;
};

/**
 * Tri des résultats par constituant standard prioritaire comme affichés dans le filtre
 * @see server/src/Action/ConstituantStd/Liste.php
 *
 * @param data StatsInterface
 * @returns StatsInterface
 */
const sortByPriorityNutriment = (data: StatsInterface[]) => {
    if (debug) {
        console.log("Classification des constitutants (std) prioritaires", nutrimentsPriority);
    }

    return data.sort((a, b) => {
        // Ensuite, trier par secteur
        const secteurComparison = a.secteur.localeCompare(b.secteur);
        if (secteurComparison !== 0) {
            return secteurComparison;
        }

        // Puis, trier par famille
        const familleComparison = a.famille.localeCompare(b.famille);
        if (familleComparison !== 0) {
            return familleComparison;
        }

        // Trouver les identifiants prioritaires dans les sous-objets
        const aPriority = a.nutriments.find((nutriment) => nutrimentsPriority.includes(nutriment.id_constituant_std));
        const bPriority = b.nutriments.find((nutriment) => nutrimentsPriority.includes(nutriment.id_constituant_std));

        if (aPriority && bPriority) {
            // Si les deux éléments ont des identifiants prioritaires, trier par priorité
            return nutrimentsPriority.indexOf(aPriority.id_constituant_std) - nutrimentsPriority.indexOf(bPriority.id_constituant_std);
        } else if (aPriority) {
            // Si seulement a a un identifiant prioritaire, a vient avant b
            return -1;
        } else if (bPriority) {
            // Si seulement b a un identifiant prioritaire, b vient avant a
            return 1;
        } else {
            // Si aucun des deux n'a d'identifiant prioritaire, trier par ordre alphabétique
            const aOrder = a.nutriments.map((nutriment) => nutriment.nutriment).join("");
            const bOrder = b.nutriments.map((nutriment) => nutriment.nutriment).join("");
            return aOrder.localeCompare(bOrder);
        }
    });
};

/**
 * Graphique A (Un graphe secteur/nutriment dont chaque boxplot correspond à une famille)
 *
 * Sélection d'un ou plusieurs secteurs
 * + Une ou plusieurs familles
 * + un ou plusieurs nutriments
 * + une année
 */
export const createSeriesForYear = (data: StatsInterface[]) => {
    if (debug) {
        console.group("Génération createSeriesForYear (A)");
    }
    let allSeries: any[] = [],
        prepareSeries: any[] = [],
        dataSeries: any[] = [],
        categories: string[] = [],
        series: PointOptionsObject[] = [],
        lastRow: any = null,
        numChart: string | null = "",
        unites: Unite[] = [];

    data = sortByPriorityNutriment(data);

    // Préparation des données
    data.forEach((row: StatsInterface, kr: number) => {
        row.nutriments.forEach((nutriment: nutrimentsInterface, kn: number) => {
            lastRow = `${row.secteur_id}-${nutriment.nutriment}`;

            if (!dataSeries[lastRow]) {
                dataSeries[lastRow] = {};
            }
            if (!dataSeries[lastRow][row.famille]) {
                dataSeries[lastRow][row.famille] = [];
            }

            dataSeries[lastRow][row.famille].push({
                nutriment,
                row: row,
            });

            let currentUnite: Unite = {
                unite: nutriment.unite,
                type: nutriment.type_unite,
            };
            if (!unites.some((unite: Unite) => JSON.stringify(unite) === JSON.stringify(currentUnite))) {
                unites.push(currentUnite);
            }
        });
    });

    // Découpage des graphes
    let devidedSeries = devideSerieSecteur(Object.values(dataSeries));

    // Création des graphes
    devidedSeries.forEach((value: DevideSerieInterface) => {
        let gMin: number = 0,
            gMax: number = 0,
            first: boolean = true;
        prepareSeries = [];
        unites = [];

        // Récupération gMin et gMax
        value.rows.forEach((secteurNutriment: any) => {
            categories = [];

            Object.values(secteurNutriment).forEach((familleId: any) => {
                familleId.forEach((nutriment: any) => {
                    if (first) {
                        gMin = nutriment.nutriment.quartiles.min || 0;
                        first = false;
                    }
                    [gMin, gMax] = getMinMaxSeries(nutriment.nutriment, gMin, gMax);

                    // Recherche les unités du graphe
                    let currentUnite: Unite = {
                        unite: nutriment.nutriment.unite,
                        type: nutriment.nutriment.type_unite,
                    };
                    if (!unites.some((unite: Unite) => JSON.stringify(unite) === JSON.stringify(currentUnite))) {
                        unites.push(currentUnite);
                    }
                });
            });
        });

        value.rows.forEach((secteurNutriment: any, ksn: number) => {
            let outliersPos: number = 0,
                outliers: object[] = [],
                average: object[] = [],
                lastData: any = {};
            categories = [];
            numChart = "";

            if (value.totalSeries > 1) {
                numChart = ` ${ksn + 1}/${value.totalSeries}`;
            }

            Object.values(secteurNutriment).forEach((familleId: any, kr: number) => {
                familleId.forEach((nutriment: any) => {
                    categories.push(
                        `${nutriment.row.famille} (${nutriment.row.secteur}) - ${nutriment.row.annee_collecte}<br/>n: ${nutriment.row.nb_produits}`
                    );

                    series.push(formatBoxPlot(nutriment.nutriment));
                    nutriment.nutriment.quartiles.outliers.map((out: any) => {
                        return outliers.push({ x: outliersPos, y: out });
                    }, outliersPos);
                    average.push({
                        x: outliersPos,
                        y: nutriment.nutriment.quartiles.average,
                    });
                    outliersPos++;
                    lastData = nutriment;
                });
            });

            // Paramètres dynamiques de la config Highcharts
            const parametersChart: ParametersChartsInterface = {
                chartTitle: `Distribution des teneurs en ${lastData.nutriment.nutriment} <br/> par famille au sein du secteur des <br/>${lastData.row.secteur} ${numChart}`,
                yAxisTitle: getYLegend(unites),
                seriesTitle: `Observations pour ${lastData.row.secteur}`,
                outliers: outliers,
                average: average,
                series: series,
                yMin: gMin,
                yMax: gMax,
                exportData: {
                    labels: categories,
                    header: {
                        code: "Code",
                        label: "Famille (Secteur) - Année",
                        effectif: "Effectif",
                    },
                    title: `Distribution par famille - nutriment - année ${numChart}`,
                },
            };

            // Génération de la config avec les précédents paramètres
            prepareSeries.push(generateConfigChart(parametersChart));
            series = [];
        });
        allSeries.push(prepareSeries);
    });

    if (debug) {
        console.log("Données entrées : ", data);
        console.log("Données compilées : ", dataSeries);
        console.groupEnd();
    }
    return allSeries;
};

/**
 * Graphique B (Un graphe secteur/nutriment dont chaque boxplot correspond à une famille)
 *
 * Sélection d'un ou plusieurs secteurs
 * + Une ou plusieurs familles
 * + un ou plusieurs nutriments
 * + plusieurs années
 */
export const createSeriesByYears = (data: StatsInterface[]) => {
    if (debug) {
        console.group("Génération createSeriesByYears (B)");
    }
    let allSeries: any[] = [],
        prepareSeries: any[] = [],
        dataSeries: any[] = [],
        categories: string[] = [],
        series: PointOptionsObject[] = [],
        lastRow: any = null,
        lastYear: any = null,
        numChart: string | null = "",
        unites: Unite[] = [];

    data = sortByPriorityNutriment(data);

    // Préparation des données
    data.forEach((row: StatsInterface, kr: number) => {
        row.nutriments.forEach((nutriment: nutrimentsInterface, kn: number) => {
            lastRow = `${row.secteur_id}-${nutriment.nutriment}`;
            lastYear = `${row.famille_id}-${row.annee_collecte}`;

            if (!dataSeries[lastRow]) {
                dataSeries[lastRow] = {};
            }
            if (!dataSeries[lastRow][lastYear]) {
                dataSeries[lastRow][lastYear] = [];
            }

            dataSeries[lastRow][lastYear].push({
                nutriment,
                row: row,
            });
        });
    });

    // Découpage des graphes
    let devidedSeries = devideSerieSecteur(Object.values(dataSeries));

    // Création des graphes
    devidedSeries.forEach((value: DevideSerieInterface) => {
        let gMin: number = 0,
            gMax: number = 0,
            first: boolean = true;
        prepareSeries = [];
        unites = [];

        // Récupération gMin et gMax
        value.rows.forEach((familleNutriment: any) => {
            categories = [];

            Object.values(familleNutriment).forEach((marqueAnnee: any) => {
                marqueAnnee.forEach((nutriment: any) => {
                    if (first) {
                        gMin = nutriment.nutriment.quartiles.min || 0;
                        first = false;
                    }
                    [gMin, gMax] = getMinMaxSeries(nutriment.nutriment, gMin, gMax);

                    // Recherche les unités du graphe
                    let currentUnite: Unite = {
                        unite: nutriment.nutriment.unite,
                        type: nutriment.nutriment.type_unite,
                    };
                    if (!unites.some((unite: Unite) => JSON.stringify(unite) === JSON.stringify(currentUnite))) {
                        unites.push(currentUnite);
                    }
                });
            });
        });

        value.rows.forEach((familleNutriment: any, ksn: number) => {
            let outliersPos: number = 0,
                outliers: object[] = [],
                average: object[] = [],
                lastData: any = {};

            categories = [];

            if (value.totalSeries > 1) {
                numChart = ` ${ksn + 1}/${value.totalSeries}`;
            }

            Object.values(familleNutriment).forEach((marqueAnnee: any) => {
                marqueAnnee.forEach((nutriment: any) => {
                    categories.push(
                        `${nutriment.row.famille} (${nutriment.row.secteur}) - ${nutriment.row.annee_collecte}<br/>n: ${nutriment.row.nb_produits}`
                    );
                    series.push(formatBoxPlot(nutriment.nutriment));
                    nutriment.nutriment.quartiles.outliers.map((out: any) => {
                        return outliers.push({ x: outliersPos, y: out });
                    }, outliersPos);
                    average.push({
                        x: outliersPos,
                        y: nutriment.nutriment.quartiles.average,
                    });
                    outliersPos++;
                    lastData = nutriment;
                });
            });

            // Paramètres dynamiques de la config Highcharts
            const parametersChart: ParametersChartsInterface = {
                chartTitle: `Distribution des teneurs en ${lastData.nutriment.nutriment} <br/>par famille et année au sein du secteur des <br/>${lastData.row.secteur} ${numChart}`,
                yAxisTitle: getYLegend(unites),
                outliers: outliers,
                average: average,
                series: series,
                seriesTitle: `Observations pour ${lastData.row.famille} ${lastData.row.annee_collecte}`,
                yMin: gMin,
                yMax: gMax,
                exportData: {
                    labels: categories,
                    header: {
                        code: "Code",
                        label: "Famille (Secteur) - Année",
                        effectif: "Effectif",
                    },
                    title: `Distribution par famille - nutriment - année ${numChart}`,
                },
            };

            // Génération de la config avec les précédents paramètres
            prepareSeries.push(generateConfigChart(parametersChart));
            series = [];
        });
        allSeries.push(prepareSeries);
    });

    if (debug) {
        console.log("Données entrées : ", data);
        console.log("Données compilées : ", dataSeries);
        console.groupEnd();
    }

    return allSeries;
};

/**
 * Graphique C (Un graphe par nutriment dont chaque boxplot correspond à une famille et une année)
 *
 * Sélection d'une ou plusieurs familles
 * + un ou plusieurs nutriments
 * + une ou plusieurs années
 * + SANS secteur
 */
export const createSeriesBySecteurs = (data: StatsInterface[]) => {
    if (debug) {
        console.group("Génération createSeriesBySecteurs (C)");
    }

    let allSeries: any[] = [],
        prepareSeries: any[] = [],
        dataSeries: any[] = [],
        categories: string[] = [],
        series: PointOptionsObject[] = [],
        lastRow: any = null,
        lastYear: any = {},
        numChart: string | null = "",
        unites: Unite[] = [],
        gMin: number = 0,
        gMax: number = 0,
        first: boolean = true;

    data = sortByPriorityNutriment(data);

    // Préparation des données
    data.forEach((row: StatsInterface, kr: number) => {
        if (first) {
            gMin = row.nutriments[0].quartiles.min || 0;
            first = false;
        }
        [gMin, gMax] = getMinMaxSeries(row.nutriments, gMin, gMax);

        row.nutriments.forEach((nutriment: nutrimentsInterface, kn: number) => {
            lastRow = `${nutriment.nutriment}`;
            lastYear = `${row.famille}-${row.annee_collecte}`;

            if (!dataSeries[lastRow]) {
                dataSeries[lastRow] = {};
            }
            if (!dataSeries[lastRow][lastYear]) {
                dataSeries[lastRow][lastYear] = [];
            }

            dataSeries[lastRow][lastYear].push({
                nutriment,
                row: row,
            });
        });
    });

    // Découpage des graphes
    let devidedSeries = devideSerieSecteur(Object.values(dataSeries));

    // Création des graphes
    devidedSeries.forEach((value: DevideSerieInterface) => {
        let gMin: number = 0,
            gMax: number = 0,
            first: boolean = true;
        prepareSeries = [];
        unites = [];

        // Récupération gMin et gMax
        value.rows.forEach((familleNutriment: any) => {
            categories = [];

            Object.values(familleNutriment).forEach((marqueAnnee: any) => {
                marqueAnnee.forEach((nutriment: any) => {
                    if (first) {
                        gMin = nutriment.nutriment.quartiles.min || 0;
                        first = false;
                    }
                    [gMin, gMax] = getMinMaxSeries(nutriment.nutriment, gMin, gMax);

                    // Recherche les unités du graphe
                    let currentUnite: Unite = {
                        unite: nutriment.nutriment.unite,
                        type: nutriment.nutriment.type_unite,
                    };
                    if (!unites.some((unite: Unite) => JSON.stringify(unite) === JSON.stringify(currentUnite))) {
                        unites.push(currentUnite);
                    }
                });
            });
        });

        value.rows.forEach((familleNutriment: any, ksn: number) => {
            let outliersPos: number = 0,
                outliers: object[] = [],
                average: object[] = [],
                lastData: any = {};
            categories = [];

            if (value.totalSeries > 1) {
                numChart = ` ${ksn + 1}/${value.totalSeries}`;
            }

            Object.values(familleNutriment).forEach((marqueAnnee: any) => {
                marqueAnnee.forEach((nutriment: any) => {
                    categories.push(
                        `${nutriment.row.famille} (${nutriment.row.secteur}) - ${nutriment.row.annee_collecte}<br/>n: ${nutriment.row.nb_produits}`
                    );
                    series.push(formatBoxPlot(nutriment.nutriment));
                    nutriment.nutriment.quartiles.outliers.map((out: any) => {
                        return outliers.push({ x: outliersPos, y: out });
                    }, outliersPos);
                    average.push({
                        x: outliersPos,
                        y: nutriment.nutriment.quartiles.average,
                    });
                    outliersPos++;
                    lastData = nutriment;
                });
            });

            // Paramètres dynamiques de la config Highcharts
            const parametersChart: ParametersChartsInterface = {
                chartTitle: `Distribution des teneurs en ${lastData.nutriment.nutriment} <br/>par famille et en année ${numChart}`,
                seriesTitle: `Observations`,
                yAxisTitle: getYLegend(unites),
                outliers: outliers,
                average: average,
                series: series,
                yMin: gMin,
                yMax: gMax,
                exportData: {
                    labels: categories,
                    header: {
                        code: "Code",
                        label: "Famille (Secteur) - Année",
                        effectif: "Effectif",
                    },
                    title: `Distribution par famille - nutriment - année ${numChart}`,
                },
            };

            // Génération de la config avec les précédents paramètres
            prepareSeries.push(generateConfigChart(parametersChart));
            series = [];
        });
        allSeries.push(prepareSeries);
    });

    if (debug) {
        console.log("Données entrées : ", data);
        console.log("Données compilées : ", dataSeries);
        console.groupEnd();
    }
    return allSeries;
};

/**
 * Graphique D (Un graphe famille/nutriment dont chaque boxplot correspond à un type de marque et une année)
 *
 * Sélection d'un ou plusieurs secteurs
 * + Une ou plusieurs familles
 * + un ou plusieurs nutriments
 * + une ou plusieurs années
 * + un ou plusieurs type de marques
 */
export const createSeriesByMarque = (data: StatsInterface[]) => {
    if (debug) {
        console.group("Génération createSeriesByMarque (D)");
    }
    let allSeries: any[] = [],
        prepareSeries: any[] = [],
        dataSeries = new Map<string, Map<string, any[]>>(), // Utilisation de Map
        categories: string[] = [],
        series: PointOptionsObject[] = [],
        lastRow: string | null = null,
        lastYear: string | null = null,
        numChart: string | null = "",
        unites: Unite[] = [],
        gMin: number = 0,
        gMax: number = 0,
        first: boolean = true;

    data = sortByPriorityNutriment(data);

    // Préparation des données
    data.forEach((row: StatsInterface) => {
        if (first) {
            gMin = row.nutriments[0].quartiles.min || 0;
            first = false;
        }
        [gMin, gMax] = getMinMaxSeries(row.nutriments, gMin, gMax);

        row.nutriments.forEach((nutriment: nutrimentsInterface) => {
            lastRow = `${row.famille_id}-${nutriment.nutriment}`;
            lastYear = `${row.type_marque}-${row.annee_collecte}`;

            if (!dataSeries.has(lastRow)) {
                dataSeries.set(lastRow, new Map());
            }
            const yearMap = dataSeries.get(lastRow)!;
            if (!yearMap.has(lastYear)) {
                yearMap.set(lastYear, []);
            }
            yearMap.get(lastYear)!.push({ nutriment, row });
        });
    });

    // Découpage des graphes
    let devidedSeries = devideSerieSecteurMap(dataSeries);

    // Création des graphes
    devidedSeries.forEach((value: DevideSerieInterface) => {
        let gMin: number = 0,
            gMax: number = 0,
            first: boolean = true;
        prepareSeries = [];
        unites = [];

        // Récupération gMin et gMax
        value.rows.forEach((familleNutriment: any) => {
            categories = [];

            Object.values(familleNutriment).forEach((marqueAnnee: any) => {
                marqueAnnee.forEach((nutriment: any) => {
                    if (first) {
                        gMin = nutriment.nutriment.quartiles.min || 0;
                        first = false;
                    }
                    [gMin, gMax] = getMinMaxSeries(nutriment.nutriment, gMin, gMax);

                    // Recherche les unités du graphe
                    let currentUnite: Unite = {
                        unite: nutriment.nutriment.unite,
                        type: nutriment.nutriment.type_unite,
                    };
                    if (!unites.some((unite: Unite) => JSON.stringify(unite) === JSON.stringify(currentUnite))) {
                        unites.push(currentUnite);
                    }
                });
            });
        });

        value.rows.forEach((familleNutriment: any, ksn: number) => {
            let outliersPos: number = 0,
                outliers: object[] = [],
                average: object[] = [],
                lastData: any = {};
            categories = [];

            if (value.totalSeries > 1) {
                numChart = ` ${ksn + 1}/${value.totalSeries}`;
            }

            Object.values(familleNutriment)
                .sort((a: any, b: any) => a[0].filter_marque.localeCompare(b[0].filter_marque))
                .forEach((marqueAnnee: any, kr: number) => {
                    marqueAnnee.forEach((nutriment: any) => {
                        categories.push(
                            `${nutriment.row.famille} (${nutriment.row.secteur}) - ${nutriment.row.type_marque} - ${nutriment.row.annee_collecte}<br/>n: ${nutriment.row.nb_produits}`
                        );
                        series.push(formatBoxPlot(nutriment.nutriment));
                        nutriment.nutriment.quartiles.outliers.forEach((out: any) => {
                            outliers.push({ x: outliersPos, y: out });
                        });
                        average.push({
                            x: outliersPos,
                            y: nutriment.nutriment.quartiles.average,
                        });
                        outliersPos++;
                        lastData = nutriment;
                    });
                });

            // Paramètres dynamiques de la config Highcharts
            const parametersChart: ParametersChartsInterface = {
                chartTitle: `Distribution des teneurs en ${lastData.nutriment.nutriment} <br/>par segment de marché <br/>Famille ${lastData.row.famille} (${lastData.row.secteur}) ${numChart}`,
                seriesTitle: `Observations pour ${lastData.row.secteur}`,
                yAxisTitle: getYLegend(unites),
                outliers: outliers,
                average: average,
                series: series,
                yMin: gMin,
                yMax: gMax,
                exportData: {
                    labels: categories,
                    header: {
                        code: "Code",
                        label: "Famille (Secteur) - Type de marque - Année",
                        effectif: "Effectif",
                    },
                    title: `Distribution par famille - nutriment - année et segment de marché ${numChart}`,
                },
            };

            // Génération de la config avec les précédents paramètres
            prepareSeries.push(generateConfigChart(parametersChart));
            series = [];
        });
        allSeries.push(prepareSeries);
    });

    if (debug) {
        console.log("Données entrées : ", data);
        console.log("Données compilées : ", dataSeries);
        console.groupEnd();
    }
    return allSeries;
};

/**
 * Graphique E (Un graphe par nutriment dont chaque boxplot correspond à une famille, une année et une marque)
 *
 * Sélection d'une ou plusieurs familles
 * + un ou plusieurs nutriments
 * + une ou plusieurs années
 * + une ou plusieurs marques
 * + SANS secteur
 */
export const createSeriesBySecteursAndMarque = (data: StatsInterface[]) => {
    if (debug) {
        console.group("Génération createSeriesBySecteursAndMarque (E)");
    }

    let allSeries: any[] = [],
        prepareSeries: any[] = [],
        dataSeries: any[] = [],
        categories: string[] = [],
        series: PointOptionsObject[] = [],
        lastRow: any = null,
        lastYear: any = {},
        numChart: string | null = "",
        unites: Unite[] = [];

    data = sortByPriorityNutriment(data);

    // Préparation des données
    data.forEach((row: StatsInterface, kr: number) => {
        row.nutriments.forEach((nutriment: nutrimentsInterface, kn: number) => {
            lastRow = `${nutriment.nutriment}`;
            lastYear = `${row.famille}-${row.type_marque}-${row.annee_collecte}`;

            if (!dataSeries[lastRow]) {
                dataSeries[lastRow] = {};
            }
            if (!dataSeries[lastRow][lastYear]) {
                dataSeries[lastRow][lastYear] = [];
            }

            dataSeries[lastRow][lastYear].push({
                nutriment,
                row: row,
            });
        });
    });

    // Découpage des graphes
    let devidedSeries = devideSerieSecteur(Object.values(dataSeries));

    // Création des graphes
    devidedSeries.forEach((value: DevideSerieInterface) => {
        let gMin: number = 0,
            gMax: number = 0,
            first: boolean = true;
        prepareSeries = [];
        unites = [];

        // Récupération gMin et gMax
        value.rows.forEach((familleNutriment: any) => {
            categories = [];

            Object.values(familleNutriment).forEach((marqueAnnee: any) => {
                marqueAnnee.forEach((nutriment: any) => {
                    if (value.totalSeries > 1) {
                        if (first) {
                            gMin = nutriment.nutriment.quartiles.min || 0;
                            first = false;
                        }
                        [gMin, gMax] = getMinMaxSeries(nutriment.nutriment, gMin, gMax);
                    }

                    // Recherche les unités du graphe
                    let currentUnite: Unite = {
                        unite: nutriment.nutriment.unite,
                        type: nutriment.nutriment.type_unite,
                    };
                    if (!unites.some((unite: Unite) => JSON.stringify(unite) === JSON.stringify(currentUnite))) {
                        unites.push(currentUnite);
                    }
                });
            });
        });

        value.rows.forEach((familleNutriment: any, ksn: number) => {
            let outliersPos: number = 0,
                outliers: object[] = [],
                average: object[] = [],
                lastData: any = {};
            categories = [];

            if (value.totalSeries > 1) {
                numChart = ` ${ksn + 1}/${value.totalSeries}`;
            }
            Object.values(familleNutriment).forEach((marqueAnnee: any) => {
                marqueAnnee.forEach((nutriment: any) => {
                    categories.push(
                        `${nutriment.row.famille} (${nutriment.row.secteur}) – ${nutriment.row.type_marque} - ${nutriment.row.annee_collecte}<br/>n: ${nutriment.row.nb_produits}`
                    );
                    series.push(formatBoxPlot(nutriment.nutriment));
                    nutriment.nutriment.quartiles.outliers.map((out: any) => {
                        return outliers.push({ x: outliersPos, y: out });
                    }, outliersPos);
                    average.push({
                        x: outliersPos,
                        y: nutriment.nutriment.quartiles.average,
                    });
                    outliersPos++;
                    lastData = nutriment;
                });
            });

            categories.sort();

            // Paramètres dynamiques de la config Highcharts
            const parametersChart: ParametersChartsInterface = {
                chartTitle: `Distribution des teneurs en ${lastData.nutriment.nutriment} <br/>par famille, par année et par segment de marché ${numChart}`,
                seriesTitle: `Observations`,
                yAxisTitle: getYLegend(unites),
                outliers: outliers,
                average: average,
                series: series,
                yMin: gMin,
                yMax: gMax,
                exportData: {
                    labels: categories,
                    header: {
                        code: "Code",
                        label: "Famille (Secteur) - Type de marque - Année",
                        effectif: "Effectif",
                    },
                    title: `Distribution par famille - nutriment - année et segment de marché ${numChart}`,
                },
            };

            // Génération de la config avec les précédents paramètres
            prepareSeries.push(generateConfigChart(parametersChart));
            series = [];
        });
        allSeries.push(prepareSeries);
    });

    if (debug) {
        console.log("Données entrées : ", data);
        console.log("Données compilées : ", dataSeries);
        console.groupEnd();
    }
    return allSeries;
};

/**
 * Génération de la configuration des graphes
 *
 * @param ParametersChartsInterface parameters
 * @returns
 */
const generateConfigChart = (parameters: ParametersChartsInterface) => {
    return {
        chart: {
            type: "boxplot",
        },
        title: {
            text: parameters.chartTitle,
        },
        legend: {
            enabled: false,
        },
        style: {
            fontSize: "30px",
        },
        xAxis: {
            labels: { enableHTML: true, style: { fontSize: fontSize, marginTop: "30px" } },
            tickInterval: 1,
            minTickInterval: 1,
            softMin: 1,
            // on force l'index à 1
            categories: parameters.series.map((out: any, i: number) => i + 1),
        },
        exportData: parameters.exportData,
        yAxis: {
            labels: { enableHTML: true, style: { fontSize: fontSize, marginTop: "30px" } },
            min: parameters.yMin || null,
            max: parameters.yMax || null,
            title: {
                style: { fontSize: fontSize },
                text: parameters.yAxisTitle,
            },
        },
        series: [
            {
                name: parameters.seriesTitle,
                data: parameters.series,
                enableMouseTracking: false,
                tooltip: {
                    useHTML: true,
                    pointFormat: `<b>{point.series.name}</b> <br>Quartile haute: {point.high}<br>Quartile basse: {point.q1}<br>Médiane: {point.median}<br>Maximum: {point.high}<br>Minimum: {point.low}<br>`,
                },
            },
            {
                name: "Valeur aberrante",
                type: "scatter",
                data: parameters.outliers, // x, y positions where 0 is the first category
                enableMouseTracking: false,
                marker: {
                    fillColor: "red",
                    lineWidth: 1,
                },
                tooltip: {
                    pointFormat: "Observation: {point.y}",
                },
            },
            {
                name: "Moyenne",
                type: "scatter",
                data: parameters.average, // x, y positions where 0 is the first category
                enableMouseTracking: false,
                marker: {
                    fillColor: "blue",
                    lineWidth: 1,
                    symbol: "diamond",
                },
                tooltip: {
                    pointFormat: "Moyenne: {point.y}",
                },
            },
        ],
        exporting: exportingOptions(),
        credits: createCredits(),
        tooltip: getLangPoints(),
    };
};

/**
 * Génération du crédit
 */
const createCredits = () => {
    return {
        enabled: false,
        text: "Source : données Oqali - Extraction du " + new Date().toLocaleDateString("fr"),
        href: "https://www.oqali.fr/",
        style: {
            fontSize: fontSize,
        },
    };
};

const exportingOptions = () => {
    return {
        enabled: false,
        allowHTML: true,
        scale: 1,
        showTable: false,
        fallbackToExportServer: false,
        sourceWidth: 800,
        chartOptions: {
            chart: {
                backgroundColor: "#ffffff",
            },
            title: {
                style: {
                    fontSize: "1.0em",
                },
            },
        },
    };
};

/**
 * Gestion de l'affichage pour les points
 */
const getLangPoints = () => {
    return {
        shared: true,
        split: false,
        useHTML: true,
        formatter: function (tooltip: any) {
            /* @ts-ignore*/
            if (this.point.isNull) {
                return "Null";
            }
            // If not null, use the default formatter
            return tooltip.defaultFormatter.call(this, tooltip);
        },
    };
};
